import { message, notification } from '../utils/globalApp';
import { trackException } from '../utils/monitoring';
import axios from 'axios';

class NotificationService {
  private static instance: NotificationService;
  private isHandlingAuthFailure = false;
  private recentErrors = new Set<string>();
  private errorTimeout: NodeJS.Timeout | null = null;

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  /**
   * Set auth failure handling state
   * When true, error notifications will be suppressed
   */
  setAuthFailureHandling(isHandling: boolean) {
    this.isHandlingAuthFailure = isHandling;
    if (isHandling) {
      // Clear recent errors when starting auth failure handling
      this.recentErrors.clear();
    }
  }

  /**
   * Check if this error should be shown
   */
  private shouldShowError(errorMsg: string, error?: unknown): boolean {
    // Never show errors during auth failure handling
    if (this.isHandlingAuthFailure) {
      return false;
    }

    // Don't show errors for cancelled requests
    if (error && axios.isCancel(error)) {
      return false;
    }

    // Don't show duplicate errors within a short time window
    if (this.recentErrors.has(errorMsg)) {
      return false;
    }

    return true;
  }

  /**
   * Mark error as shown and set up auto-cleanup
   */
  private markErrorShown(errorMsg: string) {
    this.recentErrors.add(errorMsg);

    // Clear the timeout if one exists
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }

    // Remove this error after 2 seconds to allow showing it again later
    this.errorTimeout = setTimeout(() => {
      this.recentErrors.delete(errorMsg);
    }, 2000);
  }

  /**
   * Error notification
   * Critical errors will be persistent and require manual dismissal
   * Automatically suppresses duplicate errors and errors during auth failure
   */
  error(errorMsg: string, duration = 5, error?: unknown) {
    if (!this.shouldShowError(errorMsg, error)) {
      return;
    }

    this.markErrorShown(errorMsg);

    notification.error({
      message: 'Error',
      description: errorMsg,
      duration,
    });
    trackException(new Error(errorMsg));
  }

  /**
   * Success notification
   * Short-lived toast for confirmations
   */
  success(successMsg: string, duration = 5) {
    message.success(successMsg, duration);
  }

  /**
   * Info notification
   */
  info(infoMsg: string, duration = 5) {
    message.info(infoMsg, duration);
  }

  /**
   * Warning notification
   */
  warning(warningMsg: string, duration = 5) {
    message.warning(warningMsg, duration);
  }

  /**
   * Generic method for custom notifications
   */
  custom(config: {
    type: 'success' | 'error' | 'info' | 'warning';
    message: string;
    description?: string;
    duration?: number | null;
  }) {
    const { type, ...rest } = config;
    notification[type]({ ...rest });
  }
}

// Export a singleton instance
export const Notify = NotificationService.getInstance();
